import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Sun, Cloud, CloudRain, Wind, Droplets, ArrowUp, ArrowDown, 
  Waves, Thermometer, X, CloudSun, CloudFog,
  Snowflake, CloudLightning, RefreshCw, MapPin, Search
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const STORAGE_KEY = 'fisl_meteo_spot';

// Points côtiers de référence pour déterminer si un spot est proche de la mer
const COASTAL_POINTS = [
  {lat:48.65,lon:-2.01},{lat:48.39,lon:-4.49},{lat:47.75,lon:-3.37},{lat:47.66,lon:-2.76},
  {lat:47.29,lon:-2.39},{lat:47.27,lon:-2.21},{lat:47.12,lon:-2.10},{lat:46.69,lon:-1.93},
  {lat:46.50,lon:-1.79},{lat:46.20,lon:-1.37},{lat:46.16,lon:-1.15},{lat:45.94,lon:-0.96},
  {lat:45.88,lon:-1.20},{lat:45.82,lon:-1.11},{lat:45.63,lon:-1.03},{lat:45.61,lon:-1.08},
  {lat:45.56,lon:-0.96},{lat:44.66,lon:-1.17},{lat:44.63,lon:-1.25},{lat:43.64,lon:-1.44},
  {lat:43.48,lon:-1.56},{lat:43.39,lon:-1.66},{lat:43.30,lon:5.37},{lat:43.17,lon:5.60},
  {lat:43.12,lon:5.93},{lat:43.43,lon:6.77},{lat:43.58,lon:7.12},{lat:43.70,lon:7.27},
  {lat:41.92,lon:8.74},{lat:49.63,lon:-1.62},{lat:49.49,lon:0.11},{lat:49.36,lon:0.08},
  {lat:48.84,lon:-1.60},{lat:47.87,lon:-3.92},{lat:45.00,lon:-1.20},{lat:44.86,lon:-0.57},
  {lat:14.60,lon:-61.06},{lat:16.24,lon:-61.53},
];

const MAX_COASTAL_DIST_KM = 30;

const _distKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const isCoastal = (lat, lon) => COASTAL_POINTS.some(p => _distKm(lat, lon, p.lat, p.lon) < MAX_COASTAL_DIST_KM);

// Coefficients de marée approximatifs basés sur le cycle lunaire
const getMareeData = (date) => {
  const lunarDay = Math.floor((date.getTime() / (1000 * 60 * 60 * 24)) % 29.5);
  const coefficient = Math.max(25, Math.min(120, Math.round(70 + 45 * Math.cos((lunarDay / 29.5) * 2 * Math.PI))));
  const daysSinceRef = Math.floor((date.getTime() - new Date('2025-01-01').getTime()) / (1000 * 60 * 60 * 24));
  const minuteOffset = (daysSinceRef * 50) % (12 * 60 + 25);
  const hauteurMax = (3.5 + (coefficient - 70) * 0.02).toFixed(2);
  const hauteurMin = Math.max(0.3, 1.2 - (coefficient - 70) * 0.01).toFixed(2);
  const baseTime = new Date(date); baseTime.setHours(0, 0, 0, 0);
  const marees = [];
  const fmt = d => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const intervals = [0, 6*60+12, 12*60+24, 18*60+36];
  intervals.forEach((offset, i) => {
    const t = new Date(baseTime.getTime() + (minuteOffset + offset) * 60000);
    if (t.getDate() === date.getDate()) {
      marees.push({ type: i % 2 === 0 ? 'basse' : 'haute', heure: fmt(t), hauteur: i % 2 === 0 ? hauteurMin : hauteurMax });
    }
  });
  return { coefficient, marees: marees.slice(0, 4) };
};

const getWeatherIcon = (code, isDay) => {
  if (code === 0) return isDay ? <Sun className="h-8 w-8 text-yellow-500" /> : <Sun className="h-8 w-8 text-slate-400" />;
  if (code <= 3) return <CloudSun className="h-8 w-8 text-slate-500" />;
  if (code <= 49) return <CloudFog className="h-8 w-8 text-slate-400" />;
  if (code <= 59) return <CloudRain className="h-8 w-8 text-blue-400" />;
  if (code <= 69) return <Snowflake className="h-8 w-8 text-blue-200" />;
  if (code <= 79) return <CloudRain className="h-8 w-8 text-blue-500" />;
  if (code <= 99) return <CloudLightning className="h-8 w-8 text-purple-500" />;
  return <Cloud className="h-8 w-8 text-slate-400" />;
};

const getWeatherDesc = (code) => {
  if (code === 0) return "Ciel dégagé";
  if (code <= 3) return "Partiellement nuageux";
  if (code <= 49) return "Brouillard";
  if (code <= 55) return "Bruine";
  if (code <= 65) return "Pluie";
  if (code <= 69) return "Neige";
  if (code <= 79) return "Averses";
  if (code <= 99) return "Orage";
  return "Variable";
};

// ===== CITY SEARCH COMPONENT =====
const CitySearch = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const searchCity = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=8&language=fr&format=json`);
      const data = await res.json();
      setResults((data.results || []).filter(r => r.country_code === 'FR' || r.country_code === 'GP' || r.country_code === 'MQ' || r.country_code === 'RE'));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleInput = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCity(val), 350);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="text-center mb-6">
        <MapPin className="h-12 w-12 text-sky-500 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-slate-800">Où êtes-vous ?</h3>
        <p className="text-sm text-slate-500 mt-1">Entrez le nom de votre ville pour la météo</p>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          placeholder="Tapez le nom d'une ville..."
          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300"
          autoFocus
          data-testid="city-search-input"
        />
        {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="animate-spin h-4 w-4 border-2 border-sky-500 border-t-transparent rounded-full" /></div>}
      </div>

      <div className="max-h-[50vh] overflow-y-auto space-y-1">
        {results.map((r) => (
          <button
            key={r.id}
            onClick={() => onSelect({ name: r.name, lat: r.latitude, lon: r.longitude, region: r.admin1 || '', country: r.country || 'France' })}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-sky-50 border border-transparent hover:border-sky-200 transition-all text-left"
            data-testid={`city-result-${r.id}`}
          >
            <MapPin className="h-4 w-4 text-sky-500 flex-shrink-0" />
            <div>
              <span className="text-sm font-medium text-slate-800">{r.name}</span>
              <span className="text-xs text-slate-400 ml-2">{r.admin1 || ''}{r.admin2 ? `, ${r.admin2}` : ''}</span>
            </div>
          </button>
        ))}
        {query.length >= 2 && !searching && results.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-8">Aucune ville trouvée</p>
        )}
        {query.length < 2 && (
          <p className="text-center text-sm text-slate-400 py-8">Tapez au moins 2 caractères</p>
        )}
      </div>
    </div>
  );
};

// ===== MAIN WIDGET =====
const MeteoMareeWidget = ({ isOpen, onClose }) => {
  const [location, setLocation] = useState(null);
  const [meteo, setMeteo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [coastal, setCoastal] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const spot = JSON.parse(saved);
        setLocation(spot);
        setCoastal(isCoastal(spot.lat, spot.lon));
        setShowSearch(false);
      } catch { setShowSearch(true); }
    } else {
      axios.get('/user-settings/meteo-spot').then(r => {
        if (r.data?.spot) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(r.data.spot));
          setLocation(r.data.spot);
          setCoastal(isCoastal(r.data.spot.lat, r.data.spot.lon));
        } else {
          setShowSearch(true);
        }
      }).catch(() => setShowSearch(true));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!location || !isOpen) return;
    fetchMeteo();
  }, [location, isOpen]);

  const fetchMeteo = async () => {
    if (!location) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,wind_direction_10m,is_day&daily=weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset,wind_speed_10m_max,precipitation_probability_max&timezone=Europe/Paris&forecast_days=7`
      );
      if (!res.ok) throw new Error('API error');
      setMeteo(await res.json());
    } catch { setError('Impossible de charger la météo'); }
    finally { setLoading(false); }
  };

  const saveSpot = (spot) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(spot));
    setLocation(spot);
    setCoastal(isCoastal(spot.lat, spot.lon));
    setShowSearch(false);
    axios.post('/user-settings/meteo-spot', { spot }).catch(() => {});
  };

  const changeSpot = () => { setMeteo(null); setShowSearch(true); };

  if (!isOpen) return null;

  const today = new Date();
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(today); d.setDate(d.getDate() + i); return d; });
  const selectedDate = days[selectedDay];
  const mareeData = coastal ? getMareeData(selectedDate) : null;
  const formatDate = (d) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  const getCoefColor = (c) => c >= 95 ? 'text-red-600 bg-red-100' : c >= 70 ? 'text-orange-600 bg-orange-100' : 'text-blue-600 bg-blue-100';
  const getCoefLabel = (c) => c >= 95 ? 'Vive-eau' : c >= 70 ? 'Moyenne' : 'Morte-eau';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-sky-50 to-blue-100 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-500 to-blue-600 p-4 sm:p-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <Waves className="absolute bottom-0 left-0 h-32 w-32 -mb-8 -ml-8" />
            <Sun className="absolute top-0 right-0 h-24 w-24 -mt-4 -mr-4" />
          </div>
          <div className="relative flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                <Waves className="h-6 w-6" />
                {coastal ? 'Météo & Marées' : 'Météo'}
              </h2>
              {location && !showSearch && (
                <button onClick={changeSpot} className="text-sky-100 text-sm mt-1 flex items-center gap-1 hover:text-white transition-colors" data-testid="change-spot-btn">
                  <MapPin className="h-4 w-4" />
                  {location.name}{location.region ? `, ${location.region}` : ''}
                  <span className="text-xs ml-1 bg-white/20 rounded px-1.5 py-0.5">Changer</span>
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {location && !showSearch && (
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={fetchMeteo} disabled={loading}>
                  <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              )}
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        {showSearch ? (
          <CitySearch onSelect={saveSpot} />
        ) : (
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent" />
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">
                <Cloud className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{error}</p>
                <Button onClick={fetchMeteo} className="mt-4" variant="outline">Réessayer</Button>
              </div>
            ) : (
              <>
                {/* Météo actuelle */}
                {meteo?.current && (
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm mb-4">
                    <h3 className="text-sm font-medium text-slate-500 mb-3">Conditions actuelles</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getWeatherIcon(meteo.current.weather_code, meteo.current.is_day)}
                        <div>
                          <p className="text-3xl font-bold text-slate-800">{Math.round(meteo.current.temperature_2m)}°C</p>
                          <p className="text-slate-600">{getWeatherDesc(meteo.current.weather_code)}</p>
                        </div>
                      </div>
                      <div className="text-right text-sm text-slate-600 space-y-1">
                        <p className="flex items-center justify-end gap-1"><Wind className="h-4 w-4" />{Math.round(meteo.current.wind_speed_10m)} km/h</p>
                        <p className="flex items-center justify-end gap-1"><Droplets className="h-4 w-4" />{meteo.current.relative_humidity_2m}%</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sélection du jour */}
                <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
                  {days.map((day, idx) => (
                    <button key={idx} onClick={() => setSelectedDay(idx)}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-all ${selectedDay === idx ? 'bg-sky-500 text-white shadow-md' : 'bg-white text-slate-600 hover:bg-sky-50'}`}>
                      <div className="text-xs opacity-75">{idx === 0 ? "Auj." : formatDate(day).split(' ')[0]}</div>
                      <div className="font-bold">{day.getDate()}</div>
                    </button>
                  ))}
                </div>

                {/* Prévisions du jour */}
                {meteo?.daily && (
                  <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
                    <h3 className="text-sm font-medium text-slate-500 mb-3">Prévisions - {formatDate(selectedDate)}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="text-center">
                        {getWeatherIcon(meteo.daily.weather_code[selectedDay], true)}
                        <p className="text-xs text-slate-500 mt-1">{getWeatherDesc(meteo.daily.weather_code[selectedDay])}</p>
                      </div>
                      <div className="text-center">
                        <Thermometer className="h-6 w-6 mx-auto text-orange-500" />
                        <p className="font-bold text-slate-800">{Math.round(meteo.daily.temperature_2m_max[selectedDay])}° / {Math.round(meteo.daily.temperature_2m_min[selectedDay])}°</p>
                        <p className="text-xs text-slate-500">Max / Min</p>
                      </div>
                      <div className="text-center">
                        <Wind className="h-6 w-6 mx-auto text-sky-500" />
                        <p className="font-bold text-slate-800">{Math.round(meteo.daily.wind_speed_10m_max[selectedDay])} km/h</p>
                        <p className="text-xs text-slate-500">Vent max</p>
                      </div>
                      <div className="text-center">
                        <CloudRain className="h-6 w-6 mx-auto text-blue-500" />
                        <p className="font-bold text-slate-800">{meteo.daily.precipitation_probability_max[selectedDay]}%</p>
                        <p className="text-xs text-slate-500">Pluie</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Marées (uniquement si côtier) */}
                {mareeData && (
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-medium text-slate-500 flex items-center gap-2">
                        <Waves className="h-4 w-4 text-sky-500" />
                        Marées - {formatDate(selectedDate)}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${getCoefColor(mareeData.coefficient)}`}>
                        Coef. {mareeData.coefficient} ({getCoefLabel(mareeData.coefficient)})
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {mareeData.marees.map((m, i) => (
                        <div key={i} className={`p-3 rounded-lg ${m.type === 'haute' ? 'bg-sky-50 border border-sky-200' : 'bg-amber-50 border border-amber-200'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            {m.type === 'haute' ? <ArrowUp className="h-4 w-4 text-sky-600" /> : <ArrowDown className="h-4 w-4 text-amber-600" />}
                            <span className={`text-xs font-medium ${m.type === 'haute' ? 'text-sky-600' : 'text-amber-600'}`}>
                              {m.type === 'haute' ? 'Haute' : 'Basse'}
                            </span>
                          </div>
                          <p className="text-lg font-bold text-slate-800">{m.heure}</p>
                          <p className="text-xs text-slate-500">{m.hauteur}m</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-4 text-center">
                      Données indicatives — Vérifiez les sources officielles pour la navigation
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeteoMareeWidget;
